import type { OfflineAvatarInput } from '@/app/types';
import { countLipSyncTextWeightedLength } from '@/app/utils';
import { OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH } from '../constants';

export type StudioSpeechMode = 'text' | 'audio' | 'record';
export type StudioStep = 0 | 1 | 2;
export interface StudioAction {
  id: string;
  start: number;
  end: number;
  prompt: string;
}
export type StudioActionIssue = 'required' | 'range' | 'overlap' | 'exceeds';
export const MIN_STUDIO_ACTION_DURATION = 0.5;
export const DEFAULT_STUDIO_ACTION_DURATION = 5;
export const MAX_STUDIO_ACTION_TEXT_LENGTH = 300;

export function getStudioReadiness(input: {
  hasAvatar: boolean;
  mode: StudioSpeechMode;
  script: string;
  hasAudio: boolean;
  hasRecording: boolean;
}): [boolean, boolean] {
  const textWeightedLength = countLipSyncTextWeightedLength(input.script);
  const speechReady =
    input.mode === 'text'
      ? textWeightedLength > 0 && textWeightedLength <= OFFLINE_AVATAR_MAX_TEXT_WEIGHTED_LENGTH
      : input.mode === 'audio'
        ? input.hasAudio
        : input.hasRecording;
  return [input.hasAvatar, speechReady];
}

export function getStudioSpeech(input: {
  mode: StudioSpeechMode;
  script: string;
  voice: string;
  audio: string;
  recording: string;
  audioDuration?: number;
  recordingDuration?: number;
}): OfflineAvatarInput['speech'] {
  if (input.mode === 'text') {
    return { mode: 'text', text: input.script.trim(), ...(input.voice.trim() ? { voice: input.voice.trim() } : {}) };
  }
  return input.mode === 'audio'
    ? { mode: 'audio', audio: input.audio, duration: input.audioDuration }
    : { mode: 'audio', audio: input.recording, duration: input.recordingDuration };
}

export function validateStudioActionIssues(actions: StudioAction[], duration?: number): StudioActionIssue[] {
  const issues: StudioActionIssue[] = [];
  if (actions.length === 0 || actions.some((action) => !action.prompt.trim())) issues.push('required');

  const hasInvalidRange = actions.some(
    (action) =>
      !Number.isFinite(action.start) ||
      !Number.isFinite(action.end) ||
      action.start < 0 ||
      action.end < 0 ||
      action.end <= action.start,
  );
  if (hasInvalidRange) issues.push('range');

  if (
    duration !== undefined &&
    actions.some(
      (action) =>
        (Number.isFinite(action.start) && action.start > duration) ||
        (Number.isFinite(action.end) && action.end > duration),
    )
  ) {
    issues.push('exceeds');
  }

  const validRanges = actions
    .filter(
      (action) =>
        Number.isFinite(action.start) && Number.isFinite(action.end) && action.start >= 0 && action.end > action.start,
    )
    .sort((first, second) => first.start - second.start);
  if (validRanges.some((action, index) => index > 0 && action.start < validRanges[index - 1].end)) {
    issues.push('overlap');
  }
  return issues;
}

export function validateStudioActions(actions: StudioAction[], duration?: number): StudioActionIssue | null {
  return validateStudioActionIssues(actions, duration)[0] ?? null;
}

export function appendStudioAction(actions: StudioAction[], id: string, duration?: number): StudioAction[] {
  const maxDuration = Math.max(duration === undefined ? 0xffffffff : Math.ceil(duration), MIN_STUDIO_ACTION_DURATION);
  if (actions.length === 0) return [{ id, start: 0, end: maxDuration, prompt: '' }];
  const sorted = [...actions].sort((first, second) => first.start - second.start);
  const ranges: Array<{ start: number; end: number }> = [];
  let cursor = 0;

  for (const action of sorted) {
    if (action.start - cursor >= MIN_STUDIO_ACTION_DURATION) ranges.push({ start: cursor, end: action.start });
    cursor = Math.max(cursor, action.end);
  }
  if (maxDuration - cursor >= MIN_STUDIO_ACTION_DURATION) ranges.unshift({ start: cursor, end: maxDuration });

  const range = ranges[0];
  if (!range) return actions;
  const next = {
    id,
    start: range.start,
    end: Math.min(range.start + DEFAULT_STUDIO_ACTION_DURATION, range.end),
    prompt: '',
  };
  return [...actions, next].sort((first, second) => first.start - second.start);
}
